import { formatISO, subDays } from '@cobliteam/utils/date';
import type { PaginationState, SortingState, VisibilityState } from '@tanstack/react-table';
import queryString, { type ParseOptions, type StringifyOptions } from 'query-string';
import type { IFiltersDef } from '../../types';
import FiltersDef from './FiltersDef';

const stringifyOptions: StringifyOptions = { arrayFormat: 'comma', skipNull: true, skipEmptyString: true };
const parseOptions: ParseOptions = { arrayFormat: 'comma' };

// Helper function to update URL params
const updateURLParams = (updates: Record<string, any>) => {
  const queryParams = queryString.parse(location.search, parseOptions);

  for (const [key, value] of Object.entries(updates)) {
    if (value === undefined || value === null || value === '') {
      delete queryParams[key];
    } else {
      queryParams[key] = value;
    }
  }

  const search = queryString.stringify(queryParams, stringifyOptions);
  history.replaceState(null, '', search ? `${location.pathname}?${search}` : location.pathname);
};

// Helper function to get URL params
const getURLParam = (key: string) => {
  const params = queryString.parse(location.search, parseOptions);
  return params[key];
};

const filterGetParamFilters = (): IFiltersDef[] => {
  const params = queryString.parse(location.search, parseOptions);
  return FiltersDef.map((filter) => {
    const paramValue = params[filter.paramKey];
    return paramValue ? { ...filter, value: filter?.parseToValue?.(paramValue.toString()) } : filter;
  });
};

const filterSetParamFilters = (filter: IFiltersDef) => {
  const isFilterValueArray = Array.isArray(filter.value);
  const paramValue =
    (!isFilterValueArray && filter.value !== undefined) || (isFilterValueArray && filter.value.length > 0) ? filter?.parseToParam?.(filter.value) || filter.value : undefined;
  updateURLParams({ [filter.paramKey]: paramValue });
};

const filterGetParamSearch = (): string => getURLParam('search')?.toString() || '';
const filterSetParamSearch = (search: string) => updateURLParams({ search });

const filterGetParamDates = (subtractDaysStart = 7, subtractDaysEnd = 0) => {
  const startDate = getURLParam('startDate');
  const endDate = getURLParam('endDate');

  if (startDate && endDate) {
    return {
      startDate: new Date(startDate.toString()),
      endDate: new Date(endDate.toString()),
    };
  }

  return {
    startDate: subDays(new Date(), subtractDaysStart),
    endDate: subDays(new Date(), subtractDaysEnd),
  };
};

const filterSetParamDates = (dates: { startDate: Date | number; endDate: Date | number }) => {
  updateURLParams({
    startDate: dates.startDate ? formatISO(dates.startDate) : undefined,
    endDate: dates.endDate ? formatISO(dates.endDate) : undefined,
  });
};

const filterGetParamColumns = () => getURLParam('columns')?.toString() || '';
const filterSetParamColumns = (columns: VisibilityState) => {
  const params = Object.keys(columns).filter((key) => columns[key]);
  updateURLParams({ columns: params.length ? params.join(',') : undefined });
};

const filterSetParamPagination = (pagination: PaginationState) => {
  const { pageIndex, pageSize } = pagination;
  updateURLParams({
    page: pageIndex || undefined,
    size: pageSize || undefined,
  });
};

const filterGetParamPagination = () => ({
  pageIndex: Number(getURLParam('page')) || 0,
  pageSize: Number(getURLParam('size')) || 10,
});

const filterGetParamSort = () => {
  const sort = getURLParam('sort')?.toString();
  if (!sort) return [];

  const [id, direction] = sort.split(',');
  return [{ id, desc: direction === 'desc' }];
};

const filterSetParamSort = (sort: SortingState) => {
  const sortParam = sort[0] ? `${sort[0].id},${sort[0].desc ? 'desc' : 'asc'}` : undefined;
  updateURLParams({ sort: sortParam });
};

const filterGetCustomParams = <T,>(key: string): T | undefined => getURLParam(key) as T | undefined;

const filterSetCustomParams = <T,>(key: string, value?: T) => updateURLParams({ [key]: value });

// Export all functions
export {
  filterGetCustomParams,
  filterGetParamColumns,
  filterGetParamDates,
  filterGetParamFilters,
  filterGetParamPagination,
  filterGetParamSearch,
  filterGetParamSort,
  filterSetCustomParams,
  filterSetParamColumns,
  filterSetParamDates,
  filterSetParamFilters,
  filterSetParamPagination,
  filterSetParamSearch,
  filterSetParamSort,
};
